// The write tokens of a dataset (its screen, for those who may write it): the
// ones issued, issuing a new one and revoking one.
//
// Promise:
// - A new token is shown in full once, right after it is issued, with a way to
//   copy it and the URL a script sends rows to. Closed, it is never shown again
//   (it is in the issue's answer only; the list has names and dates).
// - Revoking asks first; the row leaves the list at once.
// - Personal data moved here (`left`): it says the tokens where it was no
//   longer work and new ones are to be issued, until one is.
import { useQuery } from "@tanstack/react-query";
import { m } from "@trameria/i18n";
import { type FormEvent, useId, useState } from "react";
import { CopyButton } from "../agent-chat/message-parts";
import { useApi } from "../app/context";
import { apiErrorMessage } from "../data/errors";
import { useFormat } from "../me/profile";
import { ErrorLine, ROW_INPUT, Skeleton } from "../ui/bits";
import { Button } from "../ui/button";
import { confirmAction } from "../ui/confirm";
import {
  datasetTokensQuery,
  useIssueDatasetToken,
  useRevokeDatasetToken,
} from "./queries";

/** Where a script sends rows (shown with a new token). */
export const datasetRowsUrl = (id: string) =>
  `${window.location.origin}/api/datasets/${id}/rows`;

function Issued({
  datasetId,
  token,
  onClose,
}: {
  datasetId: string;
  token: string;
  onClose(): void;
}) {
  const url = datasetRowsUrl(datasetId);
  return (
    <div className="mt-3 rounded-[10px] border border-accent-soft-border bg-accent-soft px-4 pt-3 pb-3.5">
      <p className="text-[12px] leading-[1.6] text-body">
        {m.datasets_token_shown_once()}
      </p>
      <div className="mt-2 flex items-center gap-1.5 rounded-[7px] border border-field bg-surface py-1 pr-1 pl-[10px]">
        <code className="min-w-0 flex-1 break-all font-mono text-[12px] text-fg">
          {token}
        </code>
        <CopyButton text={token} />
      </div>
      <div className="mt-3 text-[11.5px] text-faint">
        {m.datasets_token_endpoint()}
      </div>
      <div className="mt-1 flex items-center gap-1.5">
        <code className="min-w-0 flex-1 break-all font-mono text-[11.5px] text-muted">
          POST {url}
        </code>
        <CopyButton text={url} />
      </div>
      <div className="mt-3 flex justify-end">
        <Button onClick={onClose}>{m.datasets_token_close()}</Button>
      </div>
    </div>
  );
}

function IssueForm({
  datasetId,
  onIssued,
  onCancel,
}: {
  datasetId: string;
  onIssued(token: string): void;
  onCancel(): void;
}) {
  const issue = useIssueDatasetToken(datasetId);
  const [name, setName] = useState("");
  const id = useId();
  const title = m.datasets_token_issue();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    issue.mutate(
      { name: name.trim() },
      { onSuccess: (issued) => onIssued(issued.token) },
    );
  };
  return (
    <form
      aria-label={title}
      onSubmit={submit}
      className="mt-3 rounded-[10px] border border-panel-border px-4 pt-3 pb-4"
    >
      <label htmlFor={id} className="text-[11.5px] text-muted">
        {m.datasets_token_name()}
      </label>
      <input
        id={id}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={m.datasets_token_name_placeholder()}
        maxLength={100}
        className={`${ROW_INPUT} mt-1`}
      />
      {issue.isError && (
        <ErrorLine>
          {apiErrorMessage(issue.error, m.datasets_token_failed())}
        </ErrorLine>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          {m.common_cancel()}
        </Button>
        <Button type="submit" variant="primary" disabled={issue.isPending}>
          {m.datasets_token_issue_action()}
        </Button>
      </div>
    </form>
  );
}

export function DatasetTokens({
  datasetId,
  left = false,
}: {
  datasetId: string;
  /** Personal data moved here; the tokens where it was are revoked. */
  left?: boolean;
}) {
  const tokens = useQuery(datasetTokensQuery(useApi(), datasetId));
  const revoke = useRevokeDatasetToken(datasetId);
  const format = useFormat();
  const [issuing, setIssuing] = useState(false);
  // The token just issued: here only, never in the cache.
  const [issued, setIssued] = useState<string | null>(null);

  const askRevoke = async (token: { id: string; name: string }) => {
    const ok = await confirmAction({
      title: m.datasets_token_revoke_label({ name: token.name }),
      body: m.datasets_token_revoke_body(),
      action: m.datasets_token_revoke(),
      danger: true,
    });
    if (ok) revoke.mutate(token.id);
  };

  return (
    <section className="mt-10">
      <div className="flex items-center gap-2">
        <h2 className="min-w-0 flex-1 text-[13px] text-fg">
          {m.datasets_tokens()}
        </h2>
        {!issuing && issued === null && (
          <Button onClick={() => setIssuing(true)}>
            {m.datasets_token_issue()}
          </Button>
        )}
      </div>
      <p className="mt-1 text-[11.5px] leading-[1.6] text-faint">
        {m.datasets_tokens_note()}
      </p>
      {left && (
        <div className="mt-2 text-[12px] leading-[1.6] text-body [text-wrap:pretty] [word-break:auto-phrase]">
          <p>{m.datasets_tokens_left()}</p>
          <p>{m.datasets_tokens_left_next()}</p>
        </div>
      )}
      {issuing && (
        <IssueForm
          datasetId={datasetId}
          onCancel={() => setIssuing(false)}
          onIssued={(token) => {
            setIssuing(false);
            setIssued(token);
          }}
        />
      )}
      {issued !== null && (
        <Issued
          datasetId={datasetId}
          token={issued}
          onClose={() => setIssued(null)}
        />
      )}
      {tokens.isPending ? (
        <div className="mt-3">
          <Skeleton lines={[50, 40]} />
        </div>
      ) : tokens.isError ? (
        <ErrorLine>
          {apiErrorMessage(tokens.error, m.datasets_tokens_failed())}
        </ErrorLine>
      ) : tokens.data.length === 0 ? (
        <p className="mt-3 text-[12px] text-faint">
          {m.datasets_tokens_none()}
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-border-soft rounded-[10px] border border-panel-border">
          {tokens.data.map((t) => (
            <li key={t.id} className="flex items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] text-fg">{t.name}</div>
                <div className="mt-px text-[11.5px] text-faint">
                  {m.datasets_token_created({ date: format.day(t.createdAt) })}
                  {" · "}
                  {t.lastUsedAt
                    ? m.datasets_token_used({
                        date: format.dateTime(t.lastUsedAt),
                      })
                    : m.datasets_token_unused()}
                </div>
              </div>
              <Button
                variant="ghost"
                aria-label={m.datasets_token_revoke_label({ name: t.name })}
                onClick={() => void askRevoke(t)}
              >
                {m.datasets_token_revoke()}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
